import { useCallback } from "react";
import { defineMessage, MessageDescriptor, useIntl } from "react-intl";

type LooseAutocomplete<T extends string> = T | (string & {});
type DeepKeyOf<T extends object> = {
    [K in keyof T & string]: T[K] extends MessageDescriptor
        ? `${K}`
        : T[K] extends object
        ? `${K}` | `${K}.${DeepKeyOf<T[K]>}`
        : `${K}`
}[keyof T & string];

export const errorMessages = {
    required_error: defineMessage({
        id: "required_error",
        defaultMessage: "This field is required"
    }),
    email_error: defineMessage({
        id: "email_error",
        defaultMessage: "Correct format: name@example.com"
    }),
    reset_password: {
        unknown_email: defineMessage({
            id: "reset_password.unknown_email",
            defaultMessage: "Unknown email address",
        }),
        other_error: defineMessage({
            id: "reset_password.other_error",
            defaultMessage: "Unexpected error when contacting the server. Please, try again later."
        }),
    }
} as const;

export type ErrorMessageKey = DeepKeyOf<typeof errorMessages>;
type FormatErrorMessage<Id> = Id | { id: Id, values?: Record<string, any> };

type ErrorMessagesHook = {
    errors: typeof errorMessages;
    getError: (key: ErrorMessageKey) => MessageDescriptor;
    getErrorDyn: (key: LooseAutocomplete<ErrorMessageKey>) => MessageDescriptor | undefined;
    formatError: (message: FormatErrorMessage<ErrorMessageKey>) => string;
    formatErrorDyn: (message: FormatErrorMessage<LooseAutocomplete<ErrorMessageKey>>) => string | undefined;
}

export const useErrorMessages = (): ErrorMessagesHook => {
    const {formatMessage} = useIntl();

    const getErrorDyn = useCallback((key: LooseAutocomplete<ErrorMessageKey>): MessageDescriptor | undefined => {
        const errorKeyParts = key.split(".");
        let current: any = errorMessages;
        for (const part of errorKeyParts) {
            if (current && typeof current === "object" && part in current) {
                current = current[part];
            } else {
                return undefined;
            }
        }
        if (typeof current === "object" && "id" in current && "defaultMessage" in current) {
            return current as MessageDescriptor;
        }
        return undefined;
    }, []);

    const getError = useCallback((key: ErrorMessageKey): MessageDescriptor => {
        const error = getErrorDyn(key);
        if (!error) {
            throw new Error(`Error message with key "${key}" not found.`);
        }
        return error;
    }, [getErrorDyn]);

    const formatErrorDyn = useCallback((message: FormatErrorMessage<LooseAutocomplete<ErrorMessageKey>>): string | undefined => {
        const error = getErrorDyn(typeof message === "string" ? message : message.id);
        if (!error) {
            return undefined;
        }
        return formatMessage(error, typeof message === "string" ? undefined : message.values);
    }, [formatMessage, getErrorDyn]);

    const formatError = useCallback((message: FormatErrorMessage<ErrorMessageKey>): string => {
        const error = getError(typeof message === "string" ? message : message.id);
        return formatMessage(error, typeof message === "string" ? undefined : message.values);
    }, [formatMessage, getError]);

    return {
        errors: errorMessages,
        getError,
        getErrorDyn,
        formatError,
        formatErrorDyn,
    };
}